import React, { useState, useEffect } from 'react';
import { askMuse, submitPrompt, getMuseHistory, deleteMuseHistory, toggleMuseFavorite, cleanupOldMuseHistory } from '../services/ai.js';
import { checkAndUnlockBadges, checkAndIncrementMuseUsage } from '../services/firestore.js';
import { Sparkles, Loader2, History, Copy, Plus, Send, Award, Trash2, Star, Zap, Lock } from 'lucide-react';

export default function MuseAI({ user }) {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('inspire'); // 'inspire' or 'history'
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [badgeNotification, setBadgeNotification] = useState(null);

    // Rate Limiting State
    const [credits, setCredits] = useState({ remaining: 5, limit: 5 }); // Default fallback
    const [outOfCredits, setOutOfCredits] = useState(false);

    // Rate Limiting & Tier Logic
    const getTierLimit = () => {
        if (!user) return 5;
        if (user.isSuperAdmin) return 9999;
        if (user.isAdmin) return 30;
        if (user.subscriptionTier === 'mega_fan') return 30;
        if (user.subscriptionTier === 'super_subscriber' || user.subscriptionTier === 'superSubscriber') return 20;
        if (user.subscriptionTier === 'subscriber') return 10;
        return 5;
    };

    const tierLimit = getTierLimit();

    useEffect(() => {
        if (activeTab === 'history' && user?.uid) {
            // Fetch history first
            getMuseHistory(user.uid).then(data => {
                setHistory(data);

                // Then run cleanup in background (Visible + Buffer of equal size)
                // e.g., Limit 5 -> Keep 5 unlocked + 5 locked = 10 non-favorites
                const retentionLimit = tierLimit * 2;
                cleanupOldMuseHistory(user.uid, retentionLimit).then(deletedCount => {
                    if (deletedCount > 0) {
                        // Refresh if items were deleted to ensure UI is in sync
                        getMuseHistory(user.uid).then(setHistory);
                    }
                });
            });
        }
    }, [activeTab, user, tierLimit]);

    // Initialize Credits logic
    useEffect(() => {
        if (!user) return;

        if (user.dailyMuseUsage && user.dailyMuseUsage.date === new Date().toDateString()) {
            const remaining = Math.max(0, tierLimit - user.dailyMuseUsage.count);
            setCredits({ remaining, limit: tierLimit });
            if (remaining <= 0) setOutOfCredits(true);
        } else {
            setCredits({ remaining: tierLimit, limit: tierLimit });
            setOutOfCredits(false);
        }
    }, [user, tierLimit]);

    const askMuseAI = async () => {
        if (!user?.uid) return;

        setLoading(true);
        setResponse(null);

        try {
            // 1. Check & Increment Credits in DB
            const usageResult = await checkAndIncrementMuseUsage(
                user.uid,
                user.subscriptionTier,
                user.isAdmin,
                user.isSuperAdmin
            );

            if (!usageResult.allowed) {
                setOutOfCredits(true);
                setCredits({ remaining: 0, limit: usageResult.limit });
                alert(`You've reached your daily limit of ${usageResult.limit} sparks! Upgrade for more.`);
                return;
            }

            // Update local UI
            setCredits({ remaining: usageResult.remaining, limit: usageResult.limit });

            // 2. Call AI (or DB based on Tier)
            // Calculate if this is the "First Gift" (Global users get 1 AI prompt/day)
            // We check this using the *user* object state (which might be slightly stale vs DB, but correct for "before this action")
            const todayStr = new Date().toDateString();
            const isFirstDailyUse = !user.dailyMuseUsage || user.dailyMuseUsage.date !== todayStr;

            const result = await askMuse(
                user.uid,
                selectedCategory,
                user.subscriptionTier,
                user.isSuperAdmin,
                isFirstDailyUse
            );
            setResponse(result);

            // Add to local history immediately
            if (result.historyItem) {
                setHistory(prev => [result.historyItem, ...prev]);
            }

            // Check for unlocked badges
            const badges = await checkAndUnlockBadges(user.uid);
            if (badges && badges.length > 0) {
                setBadgeNotification(badges[0]);
                setTimeout(() => setBadgeNotification(null), 5000);
            }
        } catch (error) {
            console.error('Error asking Muse:', error);
            alert('Failed to get inspiration. Please try again.');
        } finally {
            setLoading(false);
        }
    };



    const handleDeleteHistory = async (historyId, e) => {
        e.stopPropagation(); // Prevent potentially triggering other clicks
        if (window.confirm("Delete this inspiration from history?")) {
            try {
                await deleteMuseHistory(historyId);
                setHistory(prev => prev.filter(item => item.id !== historyId));
            } catch (error) {
                console.error("Error deleting history:", error);
            }
        }
    };

    const handleToggleFavorite = async (item, e) => {
        e.stopPropagation();

        // Check Limit for NEW favorites only
        if (!item.favorite) {
            const currentFavorites = history.filter(h => h.favorite).length;
            if (currentFavorites >= tierLimit) {
                alert(`You've reached your limit of ${tierLimit} favorites! Unfavorite an old one or upgrade to save more.`);
                return;
            }
        }

        try {
            const newStatus = await toggleMuseFavorite(item.id, item.favorite || false);
            setHistory(prev => prev.map(h =>
                h.id === item.id ? { ...h, favorite: newStatus } : h
            ));
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };

    const categories = [
        { id: 'all', label: 'Surprise Me' },
        { id: 'painting', label: 'Painting' },
        { id: 'drawing', label: 'Drawing' },
        { id: '3d', label: '3D Modeling' },
        { id: 'sculpting', label: 'Sculpting' },
        { id: 'writing', label: 'Writing' },
        { id: 'coding', label: 'Coding' },
        { id: 'ai_art', label: 'AI Art' }
    ];

    return React.createElement('div', { style: { maxWidth: '600px', margin: '0 auto', textAlign: 'center', paddingTop: '20px', position: 'relative' } },
        // Badge Notification
        badgeNotification && React.createElement('div', {
            className: 'animate-fade-in',
            style: {
                position: 'absolute',
                top: '-60px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                padding: '12px 24px',
                borderRadius: '50px',
                color: 'white',
                fontWeight: 'bold',
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                zIndex: 100,
                width: 'max-content'
            }
        },
            React.createElement(Award, { size: 20, fill: 'white' }),
            `Badge Unlocked: ${badgeNotification.name}!`
        ),
        // Tabs
        React.createElement('div', { style: { display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '30px' } },
            React.createElement('button', {
                onClick: () => setActiveTab('inspire'),
                className: activeTab === 'inspire' ? 'btn' : 'btn-ghost',
                style: { borderRadius: '20px', color: activeTab === 'inspire' ? 'white' : '#999' }
            }, "Inspire Me"),
            React.createElement('button', {
                onClick: () => setActiveTab('history'),
                className: activeTab === 'history' ? 'btn' : 'btn-ghost',
                style: { borderRadius: '20px', color: activeTab === 'history' ? 'white' : '#999', display: 'flex', gap: '6px', alignItems: 'center' }
            }, React.createElement(History, { size: 16 }), "History")
        ),

        activeTab === 'inspire' ? (
            React.createElement('div', {},
                React.createElement('div', { style: { marginBottom: '40px' } },
                    React.createElement('div', {
                        style: {
                            width: '120px',
                            height: '120px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            position: 'relative'
                        }
                    },
                        // Mascot Image
                        React.createElement('img', {
                            src: "/images/mascots/MuseThink.png",
                            alt: "MuseThink Mascot",
                            style: {
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 0 20px var(--primary-glow))' // Glow effect
                            },
                            onError: (e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex'; // Show fallback
                            }
                        }),
                        // Fallback Icon (Original)
                        React.createElement('div', {
                            style: {
                                width: '80px',
                                height: '80px',
                                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                                borderRadius: '50%',
                                display: 'none', // Hidden initially
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 0 40px var(--primary-glow)',
                                position: 'absolute' // Center it if it appears
                            }
                        }, React.createElement(Sparkles, { size: 40, color: 'white' }))
                    ),
                    React.createElement('h2', { style: { fontSize: '32px', marginBottom: '10px' } }, "I'm Muse, your creative spark."),
                    React.createElement('p', { style: { color: '#999', marginBottom: '20px' } }, "Feeling bored? Let me give you a creative challenge."),

                    // Credit Badge
                    React.createElement('div', {
                        style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: outOfCredits ? '#ef4444' : 'rgba(255,255,255,0.1)',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }
                    },
                        React.createElement(Zap, { size: 14, fill: outOfCredits ? 'white' : 'currentColor', color: outOfCredits ? 'white' : '#fbbf24' }),
                        React.createElement('span', {}, user?.isSuperAdmin ? "Unlimited Credits" : `${credits.remaining} / ${credits.limit} Daily Credits`)
                    )
                ),

                // Category Selection
                React.createElement('div', { style: { display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '30px' } },
                    categories.map(cat =>
                        React.createElement('button', {
                            key: cat.id,
                            onClick: () => setSelectedCategory(cat.id),
                            style: {
                                padding: '6px 16px',
                                borderRadius: '20px',
                                border: '1px solid var(--border)',
                                background: selectedCategory === cat.id ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                                color: selectedCategory === cat.id ? 'white' : 'var(--text-muted)',
                                cursor: 'pointer',
                                fontSize: '14px',
                                transition: 'all 0.2s'
                            }
                        }, cat.label)
                    )
                ),

                React.createElement('button', {
                    onClick: askMuseAI,
                    disabled: loading || !user?.uid || outOfCredits,
                    className: 'btn',
                    style: { fontSize: '18px', padding: '16px 32px', opacity: outOfCredits ? 0.5 : 1, cursor: outOfCredits ? 'not-allowed' : 'pointer', background: outOfCredits ? '#666' : 'var(--primary)' }
                },
                    loading ? React.createElement(Loader2, { className: 'animate-spin', size: 24 }) : React.createElement(Sparkles, { size: 24 }),
                    loading ? "Consulting the Muses..." : (outOfCredits ? "Daily Limit Reached" : "Inspire Me")
                ),

                response && React.createElement('div', { className: 'animate-fade-in glass-panel', style: { marginTop: '40px', padding: '30px', border: '1px solid var(--primary)' } },
                    React.createElement('h3', { style: { color: 'var(--secondary)', marginBottom: '10px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px' } }, "Your Challenge"),
                    React.createElement('p', { style: { fontSize: '24px', lineHeight: '1.4' } }, response.text || response)
                ),


            )
        ) : (
            React.createElement('div', { className: 'animate-fade-in', style: { textAlign: 'left' } },
                React.createElement('h3', { style: { marginBottom: '10px' } }, "Your Inspiration History"),
                React.createElement('p', { style: { fontSize: '12px', color: '#999', marginBottom: '20px' } },
                    `Viewing recent ${tierLimit} items. Favorites are always visible.`
                ),
                history.length === 0 ?
                    React.createElement('p', { style: { color: '#999', textAlign: 'center' } }, "No history yet. Go get inspired!") :
                    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
                        history.map((item, index) => {
                            const isLocked = !item.favorite && index >= tierLimit;
                            return React.createElement('div', {
                                key: item.id,
                                className: 'glass-panel',
                                style: {
                                    padding: '15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderLeft: item.favorite ? '4px solid #fbbf24' : '4px solid transparent',
                                    opacity: isLocked ? 0.5 : 1,
                                    position: 'relative'
                                }
                            },
                                React.createElement('div', { style: { flex: 1, paddingRight: '10px', filter: isLocked ? 'blur(0.5px)' : 'none' } },
                                    React.createElement('p', { style: { fontSize: '16px' } }, item.prompt),
                                    React.createElement('span', { style: { fontSize: '12px', color: '#999' } }, item.date)
                                ),
                                React.createElement('div', { style: { display: 'flex', gap: '5px', alignItems: 'center' } },
                                    isLocked && React.createElement(Lock, { size: 16, style: { marginRight: '10px', color: '#999' }, title: "Upgrade to view older history" }),

                                    React.createElement('button', {
                                        className: 'btn-ghost',
                                        style: { padding: '8px', color: item.favorite ? '#fbbf24' : '#666' },
                                        onClick: (e) => handleToggleFavorite(item, e),
                                        title: item.favorite ? "Unfavorite" : "Favorite to Unlock"
                                    }, React.createElement(Star, { size: 16, fill: item.favorite ? '#fbbf24' : 'none' })),

                                    !isLocked && React.createElement('button', {
                                        className: 'btn-ghost',
                                        style: { padding: '8px', color: '#666' },
                                        onClick: () => navigator.clipboard.writeText(item.prompt),
                                        title: "Copy"
                                    }, React.createElement(Copy, { size: 16 })),

                                    React.createElement('button', {
                                        className: 'btn-ghost',
                                        style: { padding: '8px', color: '#ef4444' },
                                        onClick: (e) => handleDeleteHistory(item.id, e),
                                        title: "Delete"
                                    }, React.createElement(Trash2, { size: 16 }))
                                )
                            );
                        })
                    )
            )
        )
    );
}
